import { fetchEllmoData, type EllmoApiType } from './shared';

export type { EllmoApiType };

// Type for Next.js App context
type AppContext = {
  Component: any;
  ctx: any;
  router: any;
};

/**
 * Helper for _app.tsx to fetch global Ellmo data and merge with page props
 * Automatically calls the page's getInitialProps and merges everything
 * 
 * @param clientId - Your Ellmo client ID
 * @param url - Function that returns the URL path from app context
 * @param type - Content type: 'semantic' or 'structured' (default: 'semantic')
 * @param debugging - Include pageEllmoError in props for debugging (default: false)
 * 
 * Returns pageEllmoData in pageProps. If debugging is enabled, also includes pageEllmoError.
 * 
 * @example
 * ```tsx
 * import { withEllmoApp } from 'ellmo-ai-react';
 * 
 * function MyApp({ Component, pageProps }) {
 *   return (
 *     <>
 *       <header>
 *         <EllmoApiSSR 
 *           data={pageProps.pageEllmoData} 
 *           error={pageProps.pageEllmoError} 
 *           showErrors={true} // Show errors in development
 *         />
 *       </header>
 *       <Component {...pageProps} />
 *     </>
 *   );
 * }
 * 
 * MyApp.getInitialProps = withEllmoApp({
 *   clientId: process.env.NEXT_PUBLIC_ELLMO_CLIENT_ID!,
 *   url: (appContext) => '/global-header',
 *   type: 'structured',
 *   debugging: process.env.NODE_ENV === 'development' // Enable error prop in development
 * });
 * 
 * export default MyApp;
 * ```
 */
export function withEllmoApp({
  clientId,
  url,
  type = 'semantic',
  debugging = false
}: {
  clientId: string;
  url: () => string;
  type?: EllmoApiType;
  debugging?: boolean;
}) {
  return async (appContext: AppContext) => {
    // Fetch global Ellmo data
    const { data, error } = await fetchEllmoData({
      clientId,
      url: url(), // Use the function to get the URL
      type
    });

    // Call the page's getInitialProps if it exists
    let pageProps = {};
    if (appContext.Component.getInitialProps) {
      pageProps = await appContext.Component.getInitialProps(appContext.ctx);
    }

    // Return merged props with ellmo data inside pageProps
    return {
      pageProps: {
        ...pageProps,
        pageEllmoData: data,
        ...(debugging ? { pageEllmoError: error } : {}), // Only add error if debugging is enabled
      }
    };
  };
}

/**
 * Higher-order function to wrap getStaticProps with Ellmo data fetching
 * Automatically calls the page's existing getStaticProps and merges Ellmo data
 *
 * @param clientId - Your Ellmo client ID
 * @param url - Function that returns the URL path from GetStaticPropsContext
 * @param type - Content type: 'semantic' or 'structured' (default: 'semantic')
 * @param debugging - Include ellmoError in props for debugging (default: false)
 *
 * Returns ellmoData and ellmoError in props at both root level and inside pageProps for flexibility.
 *
 * @example
 * ```tsx
 * import { withEllmoStatic } from 'ellmo-ai-react';
 *
 * export const getStaticProps = withEllmoStatic({
 *   clientId: process.env.ELLMO_CLIENT_ID!,
 *   url: (context) => context.params?.slug ? `/blog/${context.params.slug}` : '/',
 *   type: 'semantic',
 *   debugging: process.env.NODE_ENV === 'development'
 * });
 *
 * // In page component - access directly
 * export default function BlogPost({ ellmoData, ellmoError }) {
 *   return (
 *     <div>
 *       <EllmoApiSSR data={ellmoData} error={ellmoError} />
 *       <h1>Blog Post</h1>
 *     </div>
 *   );
 * }
 *
 * // In _app.tsx - access via pageProps
 * function MyApp({ Component, pageProps }) {
 *   return (
 *     <div>
 *       <EllmoApiSSR data={pageProps.pageEllmoData} error={pageProps.pageEllmoError} />
 *       <Component {...pageProps} />
 *     </div>
 *   );
 * }
 * ```
 */
export function withEllmoStatic<P extends Record<string, any> = Record<string, any>>({
  clientId,
  url,
  type = 'semantic',
  debugging = false
}: {
  clientId: string;
  url: () => string;
  type?: EllmoApiType;
  debugging?: boolean;
}) {
  return async () => {
    return getEllmoStaticProps({
      clientId,
      url,
      type,
      debugging
    }) as Promise<{ props: P & { ellmoData: string | null; ellmoError?: string | null; pageProps?: { pageEllmoData: string | null; pageEllmoError?: string | null } } }>;
  };
}

/**
 * Build standardized Ellmo props synchronously from provided data and error.
 * Returns ellmoData/ellmoError at root and pageEllmoData/pageEllmoError inside pageProps.
 */
// (removed buildEllmoProps to reduce surface area)

/**
 * Fetch Ellmo data and return standardized props (no Next.js dependency).
 * Accepts a plain URL string and returns the same shape as buildEllmoProps.
 */
export async function getEllmoStaticProps({
  clientId,
  url,
  type = 'semantic',
  debugging = false
}: {
  clientId: string;
  url: () => string;
  type?: EllmoApiType;
  debugging?: boolean;
}): Promise<{ props: { ellmoData: string | null; ellmoError?: string | null; pageProps: { pageEllmoData: string | null; pageEllmoError?: string | null } } }> {
  const { data, error } = await fetchEllmoData({ clientId, url: url(), type });
  const props: { ellmoData: string | null; ellmoError?: string | null; pageProps: { pageEllmoData: string | null; pageEllmoError?: string | null } } = {
    ellmoData: data,
    pageProps: {
      pageEllmoData: data
    }
  };

  if (debugging) {
    props.ellmoError = error;
    props.pageProps.pageEllmoError = error;
  }

  return { props };
}

